import React, { useContext } from 'react';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(advancedFormat);

import { Redirect, RouteComponentProps } from 'react-router-dom';
import { StaticContext } from 'react-router';

import { usePrefetchSuspense } from 'src/lib/usePrefetch';
import { useFetch } from 'src/lib/FetchContext';

import {
  Breadcrumb,
  Button,
  cn,
  convertGhostHTMLToReactComponents,
  Grid,
  Grid2,
  GridItem,
  IconPrint,
  ShareBar,
  SocialSharingContext,
  SocialSharingProvider,
  Typography
} from '@riac/design-system';
import { Spinner } from '@riac/design-system';

import { Page404 } from 'src/pages/Page404';
import { base64ToString, stringToBase64 } from 'src/lib/IsometricBase64';
import { AccountContextProvider } from 'src/lib/AccountContext';
import { Comments } from 'src/components/Comments/Comments';
import { RecentEventsView } from 'src/components/RecentEventsView/RecentEventsView';
import { GenericPageHeader } from 'src/components/GenericPageHeader';

export type GhostPostSingleProps = React.PropsWithChildren<RouteComponentProps<any, StaticContext, unknown>>;

const calculateReadingTime = (htmlContent) => {
  if (!htmlContent) return 1;
  
  const textWithoutHtml = htmlContent.replace(/<[^>]*>/g, ' ');
  const cleanText = textWithoutHtml.replace(/\s+/g, ' ').trim();
  const wordCount = cleanText.split(' ').filter(word => word.length > 0).length;
  
  // Calculate reading time based on 238 words per minute, minimum 1 minute
  return Math.max(1, Math.round(wordCount / 238));
};

const GhostPostSingle = ({ match } : GhostPostSingleProps): JSX.Element => {
  // const isBlog = match.path.includes('riac-blog');
  // const tags = isBlog ? 'blogs' : 'news';
  let type = '';
  let tags = '';
  if(match.path.includes('riac-blog')) {
    type = 'riac-blog';
    tags = 'blogs';
  } else if(match.path.includes('news')) {
    type = 'news';
    tags = 'news';
  } else if(match.path.includes('events')) {
    type = 'event';
    tags = 'events';
  } else {
    type = 'video';
    tags = 'videos';
  }

  const { fetch } = useFetch();
  if(match.params.title.includes('~')) {
    return <Redirect to={`/${type}/${match.params.title.replace(/~/g, '')}`}/>;
  }
  const { printButtonUsed } = useContext(SocialSharingContext);

  const data = usePrefetchSuspense(`ghost-post-single-${match.params.title}-${tags}`, async () => {
    // determine tags for fetch
    // Get individual blog post
    const req = await fetch(`/api/ghost/getGhostSingleData/${match.params.title}/${tags}/3`);

    const {
      success,
      blogPost,
      comments,
    } = await req.json();

    const recent = [];

    if(success === true)
    {
      blogPost.html = stringToBase64(blogPost.html);
    }

    return { success, post: blogPost, comments };
  }).read();

  let isLoading = false;
  if(data && match.params.title.replace(/~/g, '') === data?.post?.slug) {
    isLoading = false;
  } else {
    isLoading = true;
  }

  if(data?.post?.success === 'false' || data?.success === false) {
    return <Page404/>;
  }

  return (<>
    <AccountContextProvider>
      <div className={cn('hidden print:block', printButtonUsed && 'print:hidden')}>
          Please use the print button in the share bar at the top of the page.
        <div><IconPrint/></div>
      </div>
      <GenericPageHeader
        title={data?.post?.title || ''}
        retainBackgroundOnSmallScreens
        breadcrumbs={[
          { text: 'Latest News & Events', slug: '/news-and-events' },
          { text: data?.post?.title || '', slug: '' },
        ]}
        breadcrumbsAddon={<>
          <div className='justify-center gap-5 flex-row p-3 text-text-lighter hidden md:flex'>
            <ShareBar
              title={data?.post?.title || ''} image={data?.post?.image || data?.post?.feature_image || ''}
              buttonClassName='w-6 h-6 text-white'
            />
          </div>
        </>}
        titleClassName='text-[36px] leading-[40px] lg:text-[36px] lg:leading-[40px] md:leading-[40px] md:text-[36px]'
        backgroundOverlayAddon={<>
          <div className='absolute top-0 right-0 w-full max-w-[628px] bottom-0' style={{
            backgroundImage: `url(${data?.post?.image || data?.post?.feature_image || ''})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}>
            <div className='left-0 right-0 top-0 bottom-0 absolute' style={{
              background: 'linear-gradient(270deg, rgba(23, 23, 23, 0.2) 0.01%, #171717 81.54%)',
            }}/>
          </div>
        </>}
        addon={<>
          <div className='flex flex-col gap-1.5'>
            <Typography>By: {data?.post?.author_name[0] || ''}</Typography>
            <Typography>Published in <Button variant='text' className='p-0' to='/news-and-events'>RIAC Latest</Button> · {calculateReadingTime(base64ToString(data?.post?.html))} min read · {dayjs(data?.post?.published_at).format('LL')}</Typography>
          </div>
          <div className='flex justify-start gap-5 flex-row text-text-lighter md:hidden'>
            <ShareBar
              title={data?.post?.title || ''} image={data?.post?.image || data?.post?.feature_image || ''}
              buttonClassName='w-6 h-6 text-white'
            />
          </div>
        </>}
      />
      <Grid2 className='py-20'>
        <Spinner loading={isLoading} size='lg'>
          <div className='flex flex-col gap-10'>
            {data?.post?.html
              && <div className='prose max-w-[794px] text-text font-expressive-text'>
                {convertGhostHTMLToReactComponents(base64ToString(data?.post?.html))}
              </div>
            }
          </div>
        </Spinner>
      </Grid2>
      <Grid2 className='py-20'>
        {data?.post?.tags && data?.post?.tags.includes('blogs') ? (
          <div className='flex justify-center px-4'>
            <Comments post={data?.post} comments={data?.comments} className='flex-grow-0 basis-[794px]'/>
          </div>
        ) : ''}
      </Grid2>
      <Grid2>
        <RecentEventsView postType={data?.post?.tags.includes('blogs') ? 'blogs' : data?.post?.tags.includes('news') ? 'news' : 'videos'}/>
      </Grid2>
      <div className='flex justify-center w-full'>
        <Button variant='secondary' to='/news-and-events'>View All Posts</Button>
      </div>
      {(!isLoading && data?.post?.success) && <Page404/>}
    </AccountContextProvider>
  </>);
};

export default (props: GhostPostSingleProps): JSX.Element => {
  return (
    <SocialSharingProvider>
      <GhostPostSingle {...props}/>
    </SocialSharingProvider>
  );
};
